import React, { memo, useCallback } from 'react'
import tw from 'twin.macro'
import { Checkbox, FormControlLabel, Typography } from '@mui/material'
import { useSelector } from 'react-redux'
import type { GigsafePermission } from 'src/interfaces/gigsafeUser'
import { getGigsafePermissions, getGigsafePermissionsLoading } from 'src/selectors/gigsafeUser'
import { humanizeSnakeCase } from 'src/utils/humanizeSnakeCase'

interface Props {
  value: GigsafePermission[]
  disabled: boolean
  onChange(permissions: GigsafePermission[]): void
}

/**
 * `GET /v1/internal/partners/:partnerId/roles/permissions/all` is already flattened and de-duplicated by
 * `flattenAssignablePermissions` in the saga, so this renders one flat list in the backend's own
 * order — which is what Retool shows.
 *
 * Labels come from `humanizeSnakeCase`, not a curated map: the set is ~90 values, a map would
 * need maintaining in lockstep with the backend, and a hand-written description per permission
 * would be asserting behaviour inferred from a key name.
 */
const GigsafePermissionsFieldComponent = ({ value, disabled, onChange }: Props) => {
  const permissions = useSelector(getGigsafePermissions)
  const isLoading = useSelector(getGigsafePermissionsLoading)

  const handleToggle = useCallback(
    (permission: GigsafePermission) => {
      if (value.includes(permission)) {
        onChange(
          value.filter((selected) => {
            return selected !== permission
          }),
        )

        return
      }

      onChange([...value, permission])
    },
    [value, onChange],
  )

  if (isLoading && permissions.length === 0) {
    return (
      <Typography variant="body2" css={tw`text-shades-500`}>
        Loading permissions…
      </Typography>
    )
  }

  if (permissions.length === 0) {
    return (
      <Typography variant="body2" css={tw`text-shades-500`}>
        No assignable permissions available.
      </Typography>
    )
  }

  return (
    <div
      css={tw`max-h-64 overflow-y-auto rounded-lg border border-solid border-shades-100 p-2`}
      data-testid="gigsafe-role-permissions-field"
    >
      {permissions.map((permission) => {
        return (
          <FormControlLabel
            key={permission}
            css={tw`flex`}
            control={
              <Checkbox
                size="small"
                checked={value.includes(permission)}
                disabled={disabled}
                onChange={() => {
                  handleToggle(permission)
                }}
                inputProps={
                  {
                    'data-testid': `gigsafe-role-permission-${permission}`,
                  } as React.InputHTMLAttributes<HTMLInputElement>
                }
              />
            }
            label={<Typography variant="body2">{humanizeSnakeCase(permission)}</Typography>}
          />
        )
      })}
    </div>
  )
}

export const GigsafePermissionsField = memo(GigsafePermissionsFieldComponent)
